import React, { useCallback, useMemo } from 'react';
import {
  Platform,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';

import {
  racksCloneForCreate,
  racksCreateDraft,
  racksEnsureNameForCreate,
} from './subsections/racksEngineBindings';
import { RacksInstanceEditFormBody } from './subsections/RacksInstanceEditFormBody';
import {
  isRackPowerPersistedProps,
  type RackPowerProps,
  type RackPowerPersistedProps,
  type RackPowerInitialProps,
} from './useRackPower';
import { useWebRackEditorStepBack } from './useWebRackEditorStepBack';
import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { buildLegacyRackFromRequestFields } from '../../../components/racks/rackListUtils';
import { StepShell } from '../../../components/StepShell';
import { useRackConfigurations } from '../../../hooks/queries/useRackConfigurations';
import {
  descriptorInstanceToRack,
  rackToDescriptorInstance,
} from '../../../services/descriptorEditSessionFlush';
import { spacing } from '../../../theme';
import {
  DescriptorEngineSection,
  type DescriptorInstance,
} from '../../sections/descriptorEngineAdapter';
import { RACKS_DESCRIPTOR } from '../../sections/racks.descriptor';

import type { RackConfigurationsSaveHandle } from './subsections/rackConfigurationsSaveHandle';
import type { RepeatableInstanceEditFormRenderProps } from '../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { RackConfiguration } from '../../../types/rack';

const RackPowerInitialLayout: React.FC<RackPowerInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  isEditing = false,
  showFullDetails = false,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode = false,
  registerRackSaveHandle,
  onRackEditorActiveChange,
}) => {
  const requestStage = String(
    formData.sysRequestStage ?? formData.stage ?? 'DRAFT',
  );
  const { titleLeading } = useWebRackEditorStepBack();

  // #997 Story 6.3 R7 — the create/draft layout runs on the descriptor engine.
  // The engine owns the list/edit-session/save/flush/attachment lifecycle
  // (6.1 per-unit scoping + 6.2 temp→real remap now run live); Racks supplies
  // its bespoke view (`RacksInstanceEditFormBody` → `RackEditFormContent`) and
  // its proven draft/name/clone semantics (`racksEngineBindings`) through the
  // engine's per-section seams, so the user-visible editor is unchanged.
  const draftRacks =
    (formData.rackConfigurations as RackConfiguration[] | undefined) ?? [];
  const draftInstances = useMemo(
    () =>
      draftRacks.map(
        rack => rackToDescriptorInstance(rack) as DescriptorInstance,
      ),
    [draftRacks],
  );
  const requestIdNum = requestId ? Number.parseInt(requestId, 10) || 0 : 0;
  const handleDraftInstancesChange = useCallback(
    (instances: DescriptorInstance[]) =>
      updateFormData(
        'rackConfigurations',
        instances.map(instance =>
          descriptorInstanceToRack(instance, requestIdNum, RACKS_DESCRIPTOR),
        ),
      ),
    [updateFormData, requestIdNum],
  );

  // Engine handle → bespoke RackConfigurationsSaveHandle (CreateRequest's
  // footer-save/dismiss/scope-removal bridge keeps its existing contract).
  const registerInstanceSaveHandle = useMemo(() => {
    if (!registerRackSaveHandle) {
      return undefined;
    }
    return (handle: RepeatableInstanceSaveHandle | null) => {
      if (!handle) {
        registerRackSaveHandle(null);
        return;
      }
      const rackHandle: RackConfigurationsSaveHandle = {
        isRackEditorOpen: handle.isInstanceEditorOpen,
        hasUnsavedRackEditor: handle.hasUnsavedInstanceEditor,
        commitOpenRackEditor: handle.commitOpenInstanceEditor,
        saveUnsavedRackIfNeeded: async () => {
          if (
            !handle.isInstanceEditorOpen() ||
            !handle.hasUnsavedInstanceEditor()
          ) {
            return true;
          }
          return handle.commitOpenInstanceEditor();
        },
        hasPendingRackChanges: () => handle.hasPendingListChanges?.() ?? false,
        flushPendingRackChanges: async () => {
          await handle.flushPendingListChanges?.();
        },
        tryDismissRackEditor: handle.tryDismissInstanceEditor,
        discardOpenRackEditor: handle.discardOpenInstanceEditor,
        // Draft list is parent-owned (formData.rackConfigurations) — scope
        // removal strips it there; the in-memory session reset is the
        // persisted-path (Phase 4b) concern.
        resetSessionAfterScopeRemoval: () => {},
      };
      registerRackSaveHandle(rackHandle);
    };
  }, [registerRackSaveHandle]);

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <RacksInstanceEditFormBody {...props} />
    ),
    [],
  );

  return (
    <StepShell
      title="Racks"
      titleLeading={titleLeading}
      testID="create-form-header-rack-power"
      
    >
      <DescriptorEngineSection
        descriptor={RACKS_DESCRIPTOR}
        requestId={requestId}
        isEditing={!disabled}
        disabled={disabled}
        requestStage={requestStage}
        showFullDetails={showFullDetails}
        draftInstances={draftInstances}
        onDraftInstancesChange={handleDraftInstancesChange}
        legacyRequestFields={formData}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        editBody={editBody}
        createDraft={racksCreateDraft}
        ensureNameForCreate={racksEnsureNameForCreate}
        cloneForCreate={racksCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onRackEditorActiveChange}
        testID="rack-configurations"
      />
    </StepShell>
  );
};

const RackPowerPersistedLayout: React.FC<RackPowerPersistedProps> = ({
  request,
  isEditing,
  editedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  offlineMode = false,
  registerInstanceSaveHandle,
  applyEditedDataPatch,
  rackConfigurationsDirty = false,
  onRackEditorActiveChange,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  onCaptionChange,
  pendingCaptionEdits,
}) => {
  const requestIdStr = String(request.id);
  const isOfflineRequest =
    request.id != null && String(request.id).startsWith('o-');
  const stage = request.stage || request.sysRequestStage;
  const requestStage = String(stage ?? 'DRAFT');
  const { titleLeading } = useWebRackEditorStepBack();

  const pdfFormData = useMemo(
    () => ({ ...(request as Record<string, unknown>), ...editedData }),
    [request, editedData],
  );

  // #997 Story 6.3 Phase 4b — the persisted layout runs on the descriptor
  // engine. Host-boundary display-list resolution mirrors the bespoke
  // subsection's `serverDisplayRacks`: while the user has rack edits this
  // session, `editedData.rackConfigurations` is authoritative (even when empty
  // — deleted-all must not resurrect); otherwise racks query > request embed >
  // legacy flat-rack synthesis (`LEGACY_RACK_SYNTHETIC_ID` flows through the
  // flush's existing legacy handling).
  const { data: queryRacks = [] } = useRackConfigurations(requestIdStr, true);
  const editedRacks = editedData?.rackConfigurations;
  const effectiveRacks = useMemo((): RackConfiguration[] => {
    if (isEditing && rackConfigurationsDirty && Array.isArray(editedRacks)) {
      return editedRacks as RackConfiguration[];
    }
    if (queryRacks.length > 0) {
      return queryRacks;
    }
    const embed = pdfFormData.rackConfigurations;
    if (Array.isArray(embed) && embed.length > 0) {
      return embed as RackConfiguration[];
    }
    const legacyRack = buildLegacyRackFromRequestFields(
      pdfFormData,
      requestIdStr,
    );
    return legacyRack ? [legacyRack] : [];
  }, [
    isEditing,
    rackConfigurationsDirty,
    editedRacks,
    queryRacks,
    pdfFormData,
    requestIdStr,
  ]);

  const legacyRequestFields = useMemo(
    () => ({ ...pdfFormData, rackConfigurations: effectiveRacks }),
    [pdfFormData, effectiveRacks],
  );

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <RacksInstanceEditFormBody {...props} />
    ),
    [],
  );

  return (
    <View style={rackPowerPersistedStyles.container}>
      <PersistedStepHeader
        title="Racks"
        titleLeading={titleLeading}
        titleLeadingRowStyle={rackPowerPersistedStyles.pageTitleRow}
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={rackPowerPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={rackPowerPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <DescriptorEngineSection
        descriptor={RACKS_DESCRIPTOR}
        requestId={requestIdStr}
        isEditing={isEditing}
        // #997 QA-C: keep view-mode rows drillable; mutations stay gated by
        // isEditing/listReadOnly, so do NOT restore !isEditing here.
        disabled={false}
        requestStage={requestStage}
        showFullDetails={showFullDetails}
        legacyRequestFields={legacyRequestFields}
        applyEditedDataPatch={applyEditedDataPatch}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={isOfflineRequest || offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        editBody={editBody}
        createDraft={racksCreateDraft}
        ensureNameForCreate={racksEnsureNameForCreate}
        cloneForCreate={racksCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onRackEditorActiveChange}
        testID="rack-configurations"
      />
    </View>
  );
};

const rackPowerPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
  pdfButtonContainer: {},
  pageTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.belowFormStepPageTitle,
    gap: spacing.xs,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: 0,
    flex: 1,
    minWidth: 0,
  },
});

export const RackPower: React.FC<RackPowerProps> = props =>
  isRackPowerPersistedProps(props) ? (
    <RackPowerPersistedLayout {...props} />
  ) : (
    <RackPowerInitialLayout {...props} />
  );

RackPower.displayName = 'RackPower';

/** @deprecated Test / caption suites — compose path only */
export const RackPowerStep = RackPowerInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const RackPowerInRequest = RackPowerPersistedLayout;
